import { useEffect, useState } from "react";
import { Copy, Link2, Mail, RefreshCw, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, dateShort } from "@/lib/api";
import { SectionTitle } from "@/components/Bits";

/** Studio control over the client's login-free invoice link. */
export default function InvoiceLinkPanel({ invoiceId, status }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/invoices/${invoiceId}/link`).then((r) => setState(r.data)).catch(() => {});

  useEffect(() => { load(); }, [invoiceId, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn, message) => {
    setBusy(true);
    try {
      await fn();
      toast.success(message);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Client link copied");
    } catch {
      toast.message(url);
    }
  };

  if (status === "Draft")
    return (
      <section data-testid="invoice-link-panel">
        <SectionTitle>Client Invoice Link</SectionTitle>
        <div className="card-surface p-6 text-sm text-fog">
          Send this invoice to create a secure link your client can open without logging in.
        </div>
      </section>
    );

  return (
    <section data-testid="invoice-link-panel">
      <SectionTitle>Client Invoice Link</SectionTitle>
      <div className="card-surface space-y-5 p-6 sm:p-7">
        {state?.active ? (
          <>
            <div className="flex items-start gap-3">
              <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-stone" strokeWidth={1.5} />
              <code className="break-all text-xs text-stone" data-testid="invoice-link-url">{state.url}</code>
            </div>
            <p className="text-xs text-fog" data-testid="invoice-link-stats">
              Created {dateShort(state.created_at)} · opened {state.view_count || 0} time
              {state.view_count === 1 ? "" : "s"}
              {state.last_viewed_at ? ` · last seen ${dateShort(state.last_viewed_at)}` : ""}
            </p>
          </>
        ) : (
          <p className="text-sm text-fog" data-testid="invoice-link-empty">
            No active link{state?.revoked_reason ? ` — ${state.revoked_reason}` : ""}. Create one to share this invoice.
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          {state?.active && (
            <button type="button" data-testid="invoice-link-copy" onClick={() => copy(state.url)}
              className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2 text-xs uppercase tracking-[0.12em] text-stone hover:border-lime hover:text-ink">
              <Copy className="h-3.5 w-3.5" strokeWidth={1.5} /> Copy
            </button>
          )}
          <button type="button" data-testid="invoice-link-send" disabled={busy}
            onClick={() => run(() => api.post(`/invoices/${invoiceId}/link/send`, {}), "Invoice link emailed to your client")}
            className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2 text-xs uppercase tracking-[0.12em] text-stone hover:border-lime hover:text-ink disabled:opacity-50">
            <Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> {state?.active ? "Resend" : "Create & email"}
          </button>
          <button type="button" data-testid="invoice-link-regenerate" disabled={busy}
            onClick={() => run(() => api.post(`/invoices/${invoiceId}/link`), "A new link is ready — the old one no longer works")}
            className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2 text-xs uppercase tracking-[0.12em] text-stone hover:border-lime hover:text-ink disabled:opacity-50">
            <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} /> {state?.active ? "Regenerate" : "Create link"}
          </button>
          {state?.active && (
            <button type="button" data-testid="invoice-link-revoke" disabled={busy}
              onClick={() => run(() => api.delete(`/invoices/${invoiceId}/link`), "Link revoked")}
              className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2 text-xs uppercase tracking-[0.12em] text-stone hover:border-clay hover:text-clay disabled:opacity-50">
              <ShieldOff className="h-3.5 w-3.5" strokeWidth={1.5} /> Revoke
            </button>
          )}
        </div>
        <p className="text-xs leading-relaxed text-fog">
          The link opens this invoice only — no login, no other client records. Amending an issued
          invoice retires the link automatically so your client never sees an outdated version.
        </p>
      </div>
    </section>
  );
}
